import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cashOperationsApi, type CashOperationDto, type CreateCashOperationRequest } from '../../api/cashOperations';
import { accountsApi } from '../../api/accounts';
import { personsApi } from '../../api/persons';
import { formatDate, monthsAgo, today } from '../../utils/formatDate';
import DataTable from '../../components/DataTable';
import DatePickerInput from '../../components/DatePickerInput';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';
import { useToast } from '../../components/Toast';
import { useReportScreen } from '../../components/ScreenContext';
import { useDocumentSerialPreview } from '../../hooks/useDocumentSerial';
import { useIsMirror } from '../../hooks/useIsMirror';

export default function CashOperationsList() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [from, setFrom] = useState(monthsAgo(3));
  const [to, setTo] = useState(today());
  const [cashType, setCashType] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [selectedOp, setSelectedOp] = useState<CashOperationDto | null>(null);
  // ADR-004: mirror workspaces (mysql, asmx-owned) are READ-ONLY — hide the
  // write affordances; the API 403s them anyway (defense in depth).
  const isMirror = useIsMirror();

  const { data: operations, isLoading } = useQuery({
    queryKey: ['cash-operations', from, to, cashType],
    queryFn: () => cashOperationsApi.list({ from, to, cashType: cashType || undefined }),
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  const { data: persons } = useQuery({
    queryKey: ['persons-cash'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => cashOperationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-operations'] });
      toast.addToast('Operation deleted', 'success');
    },
    onError: (err: Error) => toast.addToast(`Error: ${err.message}`, 'error'),
  });

  const totalCount = operations?.length ?? 0;

  useReportScreen(`Cash Operations (${totalCount})`, {
    recordCount: totalCount,
    filters: useMemo(() => ({ from, to, type: cashType }), [from, to, cashType]),
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Cash Operations</h1>
        {!isMirror && (
          <button
            className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            onClick={() => { setShowForm(true); setSelectedOp(null); }}
          >
            + New Operation
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <DatePickerInput label="From" value={from} onChange={setFrom} inputClassName="w-36" />
        <DatePickerInput label="To" value={to} onChange={setTo} inputClassName="w-36" />
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <select className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={cashType} onChange={(e) => setCashType(e.target.value)}>
            <option value="">All</option>
            <option value="income">Cash Income</option>
            <option value="spending">Cash Spending</option>
          </select>
        </div>
      </div>

      {/* Form */}
      {showForm && !isMirror && (
        <CashOperationForm
          operation={selectedOp}
          accounts={accounts ?? []}
          persons={persons ?? []}
          onSubmit={async (data) => {
            if (selectedOp) {
              await cashOperationsApi.update(selectedOp.id, {
                date: data.date,
                documentNumber: data.documentNumber,
                content: data.content,
                cashAccount: data.cashAccount,
                correspondenceAccount: data.correspondenceAccount,
                amount: data.amount,
                personId: data.personId,
              });
              toast.addToast('Operation updated', 'success');
            } else {
              await cashOperationsApi.create(data);
              toast.addToast('Operation created', 'success');
            }
            queryClient.invalidateQueries({ queryKey: ['cash-operations'] });
            setShowForm(false);
            setSelectedOp(null);
          }}
          onCancel={() => { setShowForm(false); setSelectedOp(null); }}
        />
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          columns={[
            {
              key: 'date',
              header: 'Date',
              render: (row) => formatDate(row.date),
              className: 'whitespace-nowrap',
            },
            { key: 'documentNumber', header: 'Doc #', className: 'whitespace-nowrap' },
            {
              key: 'documentType',
              header: 'Type',
              render: (row) => (
                <span className={`text-xs font-medium ${row.documentType === 'CashIncome' ? 'text-green-600' : 'text-red-600'}`}>
                  {row.documentType === 'CashIncome' ? 'Income' : 'Spending'}
                </span>
              ),
            },
            { key: 'content', header: 'Content', className: 'max-w-48 truncate' },
            {
              key: 'amount',
              header: 'Amount',
              render: (row) => <MoneyCell value={row.totalDebit || row.totalCredit} />,
              className: 'text-right',
            },
            {
              key: 'actions',
              header: '',
              render: (row) => (
                <div className="flex gap-2 justify-end">
                  {!isMirror && (
                    <>
                      <button className="text-xs text-primary-600 hover:underline"
                        onClick={(e) => { e.stopPropagation(); setSelectedOp(row); setShowForm(true); }}>
                        Edit
                      </button>
                      <button className="text-xs text-red-500 hover:underline"
                        onClick={(e) => { e.stopPropagation(); if (confirm(`Delete operation #${row.id}?`)) deleteMutation.mutate(row.id); }}>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              ),
              className: 'text-right',
            },
          ]}
          data={operations ?? []}
          keyFn={(row) => row.id}
          emptyMessage="No cash operations for this period"
        />
      </div>
    </div>
  );
}

function CashOperationForm({ operation, accounts, persons, onSubmit, onCancel }: {
  operation: CashOperationDto | null;
  accounts: { accountNumber: number; name: string }[];
  persons: { id: number; name: string }[];
  onSubmit: (data: CreateCashOperationRequest) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<CreateCashOperationRequest>({
    cashType: operation?.documentType === 'CashSpending' ? 'spending' : 'income',
    date: operation?.date ?? today(),
    documentNumber: operation?.documentNumber ?? '',
    content: operation?.content ?? '',
    cashAccount: 2410,
    correspondenceAccount: 0,
    amount: operation?.totalDebit || operation?.totalCredit || 0,
    personId: operation?.lines?.[0]?.personId ?? null,
    currencyCode: 'EUR',
    currencyRate: 1,
  });
  const [saving, setSaving] = useState(false);

  // Auto-numbering from document serial
  const serialType = form.cashType === 'spending' ? 'CashSpending' : 'CashIncome';
  const { previewNumber, autoSerialCode, autoFilled, setAutoFilled } = useDocumentSerialPreview(serialType, form.date);

  // Auto-fill document number from serial when no explicit number set
  useEffect(() => {
    if (!operation && previewNumber && !form.documentNumber && !autoFilled) {
      setForm(f => ({ ...f, documentNumber: previewNumber }));
      setAutoFilled(true);
    }
  }, [previewNumber, operation, form.documentNumber, autoFilled, setAutoFilled]);

  // Reset auto-fill when cash type changes
  useEffect(() => {
    if (!operation) {
      setAutoFilled(false);
      setForm(f => ({ ...f, documentNumber: '' }));
    }
  }, [form.cashType, operation, setAutoFilled]);

  const handleSubmit = async () => {
    setSaving(true);
    const explicitNumber = form.documentNumber.trim();
    try {
      await onSubmit({
        ...form,
        documentNumber: explicitNumber || '',
        autoSerialCode: !explicitNumber && autoSerialCode ? autoSerialCode : undefined,
      });
    } catch (err) { alert(err instanceof Error ? err.message : 'Error'); }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900 mb-3">
        {operation ? 'Edit Cash Operation' : 'New Cash Operation'}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type</label>
          <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.cashType} onChange={(e) => setForm({ ...form, cashType: e.target.value })}>
            <option value="income">Cash Income</option>
            <option value="spending">Cash Spending</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date *</label>
          <input type="date" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.date?.substring(0, 10) ?? ''} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Document Number *</label>
          <input type="text"
            className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md ${!form.documentNumber && previewNumber ? 'placeholder:text-gray-400' : ''}`}
            value={form.documentNumber} onChange={(e) => setForm({ ...form, documentNumber: e.target.value })}
            placeholder={previewNumber || 'e.g. KIO-001'} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Amount *</label>
          <input type="number" step="0.01" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.amount || ''} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Cash Account</label>
          <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.cashAccount} onChange={(e) => setForm({ ...form, cashAccount: parseInt(e.target.value) || 0 })}>
            <option value={0}>-- Select --</option>
            {accounts.filter(a => String(a.accountNumber).startsWith('24')).map(a => (
              <option key={a.accountNumber} value={a.accountNumber}>{a.accountNumber} {a.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Correspondence Account</label>
          <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.correspondenceAccount} onChange={(e) => setForm({ ...form, correspondenceAccount: parseInt(e.target.value) || 0 })}>
            <option value={0}>-- Select --</option>
            {accounts.map(a => <option key={a.accountNumber} value={a.accountNumber}>{a.accountNumber} {a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Person</label>
          <select className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.personId ?? ''} onChange={(e) => setForm({ ...form, personId: e.target.value ? parseInt(e.target.value) : null })}>
            <option value="">-- None --</option>
            {persons.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Content</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          onClick={handleSubmit}
          disabled={saving || (!form.documentNumber && !autoSerialCode) || form.amount <= 0}
        >
          {saving ? 'Saving...' : (operation ? 'Update' : 'Create')}
        </button>
        <button className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
